import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Mail, Plus, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Button from "@components/common/Button";
import { SearchableSelect } from "@components/common/SearchableSelect";
import {
  useDeleteVendor,
  usePushVendorToErp,
  useVendorDuplicateCandidates,
  useVendorList,
} from "@api/hooks";
import {
  VendorsList,
  VendorDeleteModal,
  VendorDuplicateResolutionModal,
  VendorInvoiceRulesModal,
} from "@components/Vendors";
import { usePlanFeature } from "@api/hooks/useSubscription";
import { useGetErpConnections } from "@api/hooks/useErpConnection";
import { useInviteVendor, useVendorLinks } from "@api/hooks/useVendorPortal";
import {
  useStripeConnection,
  useRefreshVendorPaymentProfile,
  useStartVendorPaymentProfile,
  useVendorPaymentProfiles,
} from "@api/hooks/usePayments";
import { isTenantAdminUser, useAuthStore } from "@store/authStore";
import type {
  ERPConnectionDetailResponse,
  VendorDuplicateCandidate,
  VendorSearchResponse,
} from "@schemas/api";
import type { VendorPaymentProfile } from "@/types/payments";

const erpConnectionLabel = (connection: ERPConnectionDetailResponse) =>
  connection.display_name ||
  connection.external_company_name ||
  `${connection.erp_type} ${connection.realm_id}`;

export const VendorRecords: React.FC = () => {
  const navigate = useNavigate();
  const [skip, setSkip] = useState(0);
  const [deletingVendor, setDeletingVendor] =
    useState<VendorSearchResponse | null>(null);
  const [invitingVendor, setInvitingVendor] =
    useState<VendorSearchResponse | null>(null);
  const [paymentsVendor, setPaymentsVendor] =
    useState<VendorSearchResponse | null>(null);
  const [invoiceRulesVendor, setInvoiceRulesVendor] =
    useState<VendorSearchResponse | null>(null);
  const [erpPushVendor, setErpPushVendor] =
    useState<VendorSearchResponse | null>(null);
  const [duplicateCandidate, setDuplicateCandidate] =
    useState<VendorDuplicateCandidate | null>(null);
  const [erpPushConnectionId, setErpPushConnectionId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [erpConnectionFilter, setErpConnectionFilter] = useState("");
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const activeSearchTerm = deferredSearchTerm.trim();
  const emptyMessage = activeSearchTerm
    ? `No vendors found for "${activeSearchTerm}".`
    : erpConnectionFilter
      ? "No vendors found for this ERP link."
      : "No vendors found. Create one to get started.";
  const currentUser = useAuthStore((state) => state.currentUser);
  const tenantAdmin = isTenantAdminUser(currentUser);

  useEffect(() => {
    setSkip(0);
  }, [activeSearchTerm, erpConnectionFilter]);

  const { data: vendorList, isLoading: isLoadingList } = useVendorList(
    skip,
    20,
    activeSearchTerm,
    erpConnectionFilter,
  );
  const { data: duplicateCandidates } = useVendorDuplicateCandidates(
    "needs_review",
    tenantAdmin,
  );
  const { data: vendorLinks } = useVendorLinks(0, 1000);
  const { data: stripeConnection } = useStripeConnection();
  const { data: paymentProfiles = [] } = useVendorPaymentProfiles();
  const deleteVendor = useDeleteVendor();
  const pushVendorToErp = usePushVendorToErp();
  const inviteVendor = useInviteVendor();
  const startVendorPaymentProfile = useStartVendorPaymentProfile();
  const refreshVendorPaymentProfile = useRefreshVendorPaymentProfile();
  const canManageVendorInvoiceRules = usePlanFeature("vendor_invoice_rules");
  const { data: erpConnections = [] } = useGetErpConnections();
  const activeErpConnections = useMemo(
    () => erpConnections.filter((connection) => connection.status === "active"),
    [erpConnections],
  );

  useEffect(() => {
    if (!erpPushVendor) return;
    setErpPushConnectionId("");
  }, [erpPushVendor]);

  const handleDelete = async () => {
    if (!deletingVendor) return;
    await deleteVendor.mutateAsync(deletingVendor.id);
    setDeletingVendor(null);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitingVendor || !inviteEmail) return;
    await inviteVendor.mutateAsync({
      vendor_record_id: invitingVendor.id,
      email: inviteEmail,
    });
    setInvitingVendor(null);
    setInviteEmail("");
  };

  const isProcessing = deleteVendor.isPending || pushVendorToErp.isPending;
  const vendorPortalStatusByVendorId = (vendorLinks?.items || []).reduce<
    Record<string, string>
  >((acc, link) => {
    acc[link.vendor_record_id] = link.status;
    return acc;
  }, {});
  const paymentProfilesByVendorId = paymentProfiles.reduce<
    Record<string, VendorPaymentProfile>
  >((acc, profile) => {
    acc[profile.vendor_id] = profile;
    return acc;
  }, {});
  const paymentsVendorProfile = paymentsVendor
    ? paymentProfilesByVendorId[paymentsVendor.id]
    : undefined;
  const isPendingVendorAction =
    paymentsVendorProfile?.status === "pending_vendor_action";
  const isVendorPaymentsReady = paymentsVendorProfile?.status === "ready";
  const canStartVendorStripeSetup =
    !isPendingVendorAction && !isVendorPaymentsReady;

  const stripeReady = stripeConnection?.status === "ready";

  const handleStartVendorPayments = async () => {
    if (!paymentsVendor) return;
    if (!canStartVendorStripeSetup) return;
    if (!stripeReady) return;

    await startVendorPaymentProfile.mutateAsync({
      vendorId: paymentsVendor.id,
      payload: {
        return_path: "/records?type=vendor",
        email: paymentsVendor.email || undefined,
        currency: paymentsVendor.currency_preference || undefined,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-content-primary">
            Vendors
          </h2>
          <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
            Manage your vendor database
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/records/vendors/new")}
          disabled={isProcessing}
        >
          <span className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create Vendor
          </span>
        </Button>
      </div>

      {tenantAdmin && duplicateCandidates && duplicateCandidates.total > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900">
                <AlertTriangle className="h-5 w-5" />
                Vendor duplicates need review
              </h2>
              <p className="mt-1 text-sm text-amber-800">
                {duplicateCandidates.total} possible duplicate{" "}
                {duplicateCandidates.total === 1 ? "group" : "groups"} from ERP
                sync.
              </p>
            </div>
            <div className="grid gap-2">
              {duplicateCandidates.items.slice(0, 3).map((candidate) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-end gap-2"
                >
                  <span className="max-w-[360px] truncate text-sm text-amber-800">
                    {candidate.vendors
                      .map((vendor) => vendor.legal_name)
                      .join(" / ")}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setDuplicateCandidate(candidate)}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-default bg-surface-base p-4 shadow-sm dark:border-default-dark dark:bg-surface-base-dark">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <div>
            <label
              htmlFor="vendor-search"
              className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark"
            >
              Search vendors
            </label>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
              <input
                id="vendor-search"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by vendor name..."
                className="input w-full pl-10 pr-10"
                aria-describedby="vendor-search-help"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-content-muted hover:bg-raised hover:text-content-primary focus:outline-none focus:ring-2 focus:ring-primary dark:hover:bg-raised-dark dark:hover:text-content-primary-dark"
                  aria-label="Clear vendor search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="vendor-erp-link-filter"
              className="block text-sm font-medium text-content-secondary dark:text-content-secondary-dark"
            >
              ERP Link
            </label>
            <SearchableSelect
              id="vendor-erp-link-filter"
              options={[
                { id: "", label: "All ERP Links" },
                ...activeErpConnections.map((connection) => ({
                  id: connection.id,
                  label: erpConnectionLabel(connection),
                })),
              ]}
              value={erpConnectionFilter}
              onChange={setErpConnectionFilter}
              placeholder="All ERP Links"
              clearable={false}
              className="mt-2"
            />
          </div>
        </div>
        <p id="vendor-search-help" className="mt-2 text-xs text-content-muted">
          Search matches vendor legal names across the full vendor database.
        </p>
      </div>

      {vendorList && (
        <VendorsList
          vendors={vendorList.items}
          total={vendorList.total}
          skip={skip}
          limit={20}
          onEdit={(vendor) => navigate(`/records/vendors/${vendor.id}/edit`)}
          onDelete={setDeletingVendor}
          onPageChange={setSkip}
          isLoading={isProcessing || isLoadingList}
          onInvite={setInvitingVendor}
          onSetupPayments={stripeReady ? setPaymentsVendor : undefined}
          onPushToErp={setErpPushVendor}
          onManageInvoiceRules={
            canManageVendorInvoiceRules ? setInvoiceRulesVendor : undefined
          }
          vendorPortalStatusByVendorId={vendorPortalStatusByVendorId}
          paymentProfilesByVendorId={paymentProfilesByVendorId}
          emptyMessage={emptyMessage}
        />
      )}

      <VendorDeleteModal
        vendor={deletingVendor}
        isLoading={deleteVendor.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeletingVendor(null)}
      />

      {erpPushVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-900">
            <h2 className="text-lg font-semibold text-content-primary dark:text-content-primary-dark">
              Connect vendor to ERP
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              Choose where {erpPushVendor.legal_name} should be created.
            </p>
            <label className="mt-4 block text-sm font-medium text-content-secondary">
              ERP company
            </label>
            <SearchableSelect
              options={[
                { id: "", label: "Choose ERP company" },
                ...activeErpConnections.map((connection) => ({
                  id: connection.id,
                  label: erpConnectionLabel(connection),
                })),
              ]}
              value={erpPushConnectionId}
              onChange={setErpPushConnectionId}
              placeholder="Choose ERP company"
              clearable={false}
              className="mt-1"
            />
            <div className="mt-5 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setErpPushVendor(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!erpPushConnectionId || pushVendorToErp.isPending}
                onClick={() =>
                  pushVendorToErp.mutate(
                    {
                      vendorId: erpPushVendor.id,
                      erpConnectionId: erpPushConnectionId,
                    },
                    { onSuccess: () => setErpPushVendor(null) },
                  )
                }
              >
                {pushVendorToErp.isPending ? "Connecting..." : "Create in ERP"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <VendorInvoiceRulesModal
        vendor={invoiceRulesVendor}
        onClose={() => setInvoiceRulesVendor(null)}
      />

      <VendorDuplicateResolutionModal
        candidate={duplicateCandidate}
        onClose={() => setDuplicateCandidate(null)}
      />

      {/* Invite Vendor Modal */}
      {invitingVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-base-dark rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Invite Vendor
              </h2>
              <button
                onClick={() => {
                  setInvitingVendor(null);
                  setInviteEmail("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Send an invitation to{" "}
              <span className="font-medium">{invitingVendor.legal_name}</span>{" "}
              to join EZ Flow as a vendor.
            </p>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vendor Email
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={invitingVendor.email || "vendor@company.com"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setInvitingVendor(null);
                    setInviteEmail("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={inviteVendor.isPending}
                >
                  <span className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {inviteVendor.isPending ? "Sending..." : "Send Invitation"}
                  </span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {paymentsVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-base-dark rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Stripe Payout Setup
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Start vendor onboarding and refresh payout readiness for{" "}
                  {paymentsVendor.legal_name}.
                </p>
              </div>
              <button
                onClick={() => setPaymentsVendor(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              <p className="font-medium">
                Tenant Stripe:{" "}
                {(stripeConnection?.status || "not_started").replace(/_/g, " ")}
              </p>
              <p className="font-medium">
                Current status:{" "}
                {(paymentsVendorProfile?.status || "not_started").replace(
                  /_/g,
                  " ",
                )}
              </p>
              <p className="mt-1">
                {!stripeReady
                  ? "Connect the tenant Stripe account from Company Profile before linking vendor payouts."
                  : isVendorPaymentsReady
                    ? "This vendor is already ready for Stripe payouts."
                    : isPendingVendorAction
                      ? "The vendor already has an active Stripe onboarding action. Refresh after they complete it."
                      : "Start or link the vendor Stripe payout account for this tenant."}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  refreshVendorPaymentProfile.mutateAsync(paymentsVendor.id)
                }
                disabled={refreshVendorPaymentProfile.isPending}
              >
                Refresh Status
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleStartVendorPayments}
                disabled={
                  startVendorPaymentProfile.isPending ||
                  !canStartVendorStripeSetup ||
                  !stripeReady
                }
              >
                {startVendorPaymentProfile.isPending
                  ? "Starting…"
                  : !stripeReady
                    ? "Tenant Stripe Not Connected"
                    : isVendorPaymentsReady
                      ? "Vendor Already Connected"
                      : isPendingVendorAction
                        ? "Waiting on Vendor"
                        : "Start Stripe Setup"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
